import React from 'react';
import styled, { css } from 'styled-components';
import { SecondaryButton } from '../button';
import { breakpoint, theme } from '../../theme';
import { ChevronLeftIcon, ChevronRightIcon } from '../../assets';
import { VisiblePagesType, visiblePages } from './util';

export type PaginationProps = {
  currentPage: number;
  onPageChange: (page: number) => void;
  pageCount: number;
};

export type PagesProps = {
  visiblePages: VisiblePagesType;
};

const Wrapper = styled.div`
  margin: ${theme.spacings[7].rem} 0 0;
  text-align: center;
`;

const Nav = styled.nav`
  align-items: center;
  display: inline-flex;
  flex-wrap: wrap;
  width: fit-content;
`;

const Pages = styled.div<PagesProps>`
  display: flex;
  margin: 0 ${theme.spacings[1].rem};

  div {
    margin: 0;
  }

  div:nth-child(-n + ${({ visiblePages }) => visiblePages.small.left}),
  div:nth-child(n + ${({ visiblePages }) => visiblePages.small.right}) {
    display: none;
  }

  ${breakpoint('sm')`
    margin: 0 ${theme.spacings[3].rem};

    div {
      margin: 0 2px;
    }
  `}

  ${breakpoint('lg')<PagesProps>`
    margin: 0 ${theme.spacings[5].rem};

    div:nth-child(n) {
      display: inline-block
    }

    div:nth-child(-n + ${({ visiblePages }) => visiblePages.large.left}),
    div:nth-child(n + ${({ visiblePages }) => visiblePages.large.right}) {
      display: none;
    }
  `}
`;

const PreviousButton = styled.div`
  margin-left: ${theme.spacings[1].rem};

  ${breakpoint('sm')`
    margin-left: ${theme.spacings[2].rem};
  `};
`;

const NextButton = styled.div`
  margin-right: ${theme.spacings[1].rem};

  ${breakpoint('sm')`
    margin-right: ${theme.spacings[2].rem};
  `};
`;

const NavLabel = styled.span`
  display: none;

  ${breakpoint('md')`
    display: inline-block;
  `};
`;

const DirectionalIconStyles = css`
  width: 24px;
  height: 24px;
  margin: -2px -8px;

  ${breakpoint('md')`
    width: 16px;
    height: 16px;
  `}
`;

const StyledChevronLeft = styled(ChevronLeftIcon)`
  ${DirectionalIconStyles};

  ${breakpoint('md')`
    margin: 0 8px 0 0;
  `}
`;

const StyledChevronRight = styled(ChevronRightIcon)`
  ${DirectionalIconStyles};

  ${breakpoint('md')`
    margin: 0 0 0 8px;
  `}
`;

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  onPageChange,
  pageCount,
}) => {
  const pages = Array.from(Array(pageCount).keys(), (k) => k + 1);

  return (
    <Wrapper>
      <Nav>
        <SecondaryButton
          disabled={currentPage === 1}
          onClick={() => onPageChange(1)}
          size="small"
        >
          First
        </SecondaryButton>

        <PreviousButton>
          <SecondaryButton
            disabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
            size="small"
          >
            <StyledChevronLeft />
            <NavLabel>Previous</NavLabel>
          </SecondaryButton>
        </PreviousButton>

        <Pages visiblePages={visiblePages(currentPage, pageCount)}>
          {pages.map((page) => (
            <SecondaryButton
              key={page}
              isActive={page === currentPage}
              onClick={() => onPageChange(page)}
              size="small"
            >
              {page}
            </SecondaryButton>
          ))}
        </Pages>

        <NextButton>
          <SecondaryButton
            disabled={currentPage === pageCount}
            onClick={() => onPageChange(currentPage + 1)}
            size="small"
          >
            <NavLabel>Next</NavLabel>
            <StyledChevronRight />
          </SecondaryButton>
        </NextButton>

        <SecondaryButton
          disabled={currentPage === pageCount}
          onClick={() => onPageChange(pageCount)}
          size="small"
        >
          Last
        </SecondaryButton>
      </Nav>
    </Wrapper>
  );
};
